class StudioVariantDropdown extends HTMLElement {
  #outsideClickHandler = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) {
      this.#close();
    }
  };

  #handleSiblingOpen = (event) => {
    if (event.detail?.source !== this) {
      this.#close();
    }
  };

  connectedCallback() {
    this.select = this.querySelector('select');
    this.trigger = this.querySelector('[data-dropdown-trigger]');
    this.list = this.querySelector('[data-dropdown-list]');
    this.valueEl = this.querySelector('[data-dropdown-value]');

    if (!this.select || !this.trigger || !this.list || !this.valueEl) return;

    this.trigger.addEventListener('click', this.#onTriggerClick);
    this.list.addEventListener('click', this.#onOptionClick);
    this.select.addEventListener('change', this.#syncFromSelect);
    this.closest('variant-picker')?.addEventListener(
      'studio-variant-dropdown:open',
      this.#handleSiblingOpen
    );
    this.#syncFromSelect();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.#onTriggerClick);
    this.list?.removeEventListener('click', this.#onOptionClick);
    this.select?.removeEventListener('change', this.#syncFromSelect);
    this.closest('variant-picker')?.removeEventListener(
      'studio-variant-dropdown:open',
      this.#handleSiblingOpen
    );
    document.removeEventListener('click', this.#outsideClickHandler);
  }

  #closeSiblingDropdowns() {
    const scope = this.closest('variant-picker') || this.closest('.product-details');
    if (!scope) return;

    scope.querySelectorAll('studio-variant-dropdown').forEach((dropdown) => {
      if (dropdown !== this) {
        dropdown.close?.();
      }
    });
  }

  #onTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.#close();
      return;
    }

    this.#closeSiblingDropdowns();
    this.#open();
  };

  #onOptionClick = (event) => {
    const option = event.target.closest('[data-dropdown-option]');
    if (!option || !this.select) return;

    const value = option.dataset.value;
    if (!value || this.select.value === value) {
      this.#close();
      return;
    }

    this.select.value = value;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.#close();
  };

  #open() {
    this.trigger.setAttribute('aria-expanded', 'true');
    this.list.hidden = false;
    this.list.style.display = 'block';
    this.setAttribute('open', '');
    this.dispatchEvent(
      new CustomEvent('studio-variant-dropdown:open', {
        bubbles: true,
        detail: { source: this },
      })
    );
    requestAnimationFrame(() => {
      document.addEventListener('click', this.#outsideClickHandler);
    });
  }

  close() {
    this.#close();
  }

  #close() {
    this.trigger.setAttribute('aria-expanded', 'false');
    this.list.hidden = true;
    this.list.style.display = '';
    this.removeAttribute('open');
    document.removeEventListener('click', this.#outsideClickHandler);
  }

  #syncFromSelect = () => {
    if (!this.select || !this.valueEl || !this.list) return;

    const selectedOption = this.select.options[this.select.selectedIndex];
    const label = selectedOption?.textContent?.replace(/\s*-\s*.*$/, '').trim() ?? '';
    this.valueEl.textContent = label;

    this.list.querySelectorAll('[data-dropdown-option]').forEach((item) => {
      const isSelected = item.dataset.value === this.select.value;
      item.classList.toggle('is-selected', isSelected);
      item.setAttribute('aria-selected', String(isSelected));
    });
  };
}

if (!customElements.get('studio-variant-dropdown')) {
  customElements.define('studio-variant-dropdown', StudioVariantDropdown);
}
